import { BaseError, BaseQuery, DBItem, OnItemsData } from '@chem-po/core'
import { addMetadata } from '@hb/react'
import {
  AnyObject,
  AppName,
  Collection,
  CombinedCollection,
  FieldMap,
  FieldMapValue,
  findFormElementsWithDataUrls,
  getBaseCollection,
  toHbQuery,
  UpdateCallback,
  UploadProgress,
  ValidationErrors,
  WithMetadata,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'
import {
  collection,
  doc,
  DocumentReference,
  deleteDoc as firebaseDeleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore'
import { set as nestedSet } from 'nested-property'
import { db } from '../db'
import { addItem, getCollectionRef } from './collections'
import { processFieldMapData } from './processData'
import { toFirestoreQuery } from './queries'

export const saveDoc = async (
  appName: AppName,
  admin: boolean,
  uid: string,
  collectionPath: string,
  id: string | null,
  field: FieldMap | null,
  data: FieldMapValue,
  onUploadProgress?: (progress: Record<string, UploadProgress>) => void,
): Promise<ValidationErrors | string> => {
  const docRef = id ? doc(db, collectionPath, id) : doc(collection(db, collectionPath))
  const docData = id ? await getDoc(docRef).then(fetched => fetched.data()) : undefined
  const baseStoragePath = `${collectionPath}/${docRef.id}`
  const processedData = field
    ? await processFieldMapData(
        appName,
        admin,
        uid,
        baseStoragePath,
        field,
        data,
        docData,
        onUploadProgress ?? (() => {}),
      )
    : data
  if (field) {
    const withDataUrls = findFormElementsWithDataUrls(field, processedData)
    if (withDataUrls.length) {
      throw new Error(
        `Data URLs found in fields: ${withDataUrls.map(f => f.fieldPathSegments.join('.')).join(', ')}`,
      )
    }
  }
  const promise = docData ? updateDoc(docRef, processedData) : setDoc(docRef, processedData)
  return promise
    .then(() => docRef.id)
    .catch(e => ({ [FORM_ERROR]: e?.message || 'An error occurred' }))
}

export const deleteDoc = async (
  appName: AppName,
  admin: boolean,
  uid: string,
  collectionPath: string,
  id: string,
  field: FieldMap | null,
) => {
  const docRef = doc(db, collectionPath, id)
  const docData = await getDoc(docRef).then(fetched => fetched.data())
  if (field) {
    const baseStoragePath = `${collectionPath}/${docRef.id}`
    await processFieldMapData(
      appName,
      admin,
      uid,
      baseStoragePath,
      field,
      undefined,
      docData,
      () => {},
    )
  }
  await firebaseDeleteDoc(docRef)
}

const updateCombinedCollectionItem = async <Data extends AnyObject = AnyObject>(
  appName: AppName,
  collection: CombinedCollection<Data>,
  id: string,
  path: string,
  data: FieldMapValue,
): Promise<void> => {
  const batch = writeBatch(db)
  const { index, otherCollections } = collection

  // make sure item exists in all collections
  const missingItems = await Promise.all(
    Object.values(otherCollections).map(async other => {
      const otherCollection = getBaseCollection(other)
      if (!otherCollection) throw new Error('No collection')
      const ref = getCollectionRef(other)
      const docRef = doc(ref, id)
      const otherDoc = await getDoc(docRef)
      return { name: other.name, exists: otherDoc.exists(), docRef }
    }),
  )

  const toBeCreated = missingItems.filter(item => !item.exists)

  await Promise.all(
    toBeCreated.map(async ({ docRef }) => {
      await setDoc(docRef, {}, { merge: true })
    }),
  )

  if (path) {
    const key = path.split('.')[0]
    if (index.propNames.includes(key as keyof Data)) {
      batch.update(doc(getCollectionRef(getBaseCollection(index)), id), path, data)
    } else {
      Object.values(otherCollections).forEach(other => {
        if (other.propNames.includes(key as keyof Data)) {
          batch.update(doc(getCollectionRef(getBaseCollection(other)), id), path, data)
        }
      })
    }
  } else {
    const updated: Record<string, AnyObject> = {}
    Object.entries(data).forEach(([key, value]) => {
      if (index.propNames.includes(key.split('.')[0] as keyof Data)) {
        updated.index = { ...updated.index, [key]: value }
      } else {
        Object.entries(otherCollections).forEach(([name, other]) => {
          if (other.propNames.includes(key.split('.')[0] as keyof Data)) {
            updated[name] = { ...updated[name], [key]: value }
          }
        })
      }
    })
    Object.entries(updated).forEach(([name, value]) => {
      if (name === 'index') {
        const baseIndexCollection = getBaseCollection(index)
        batch.update(
          doc(getCollectionRef(baseIndexCollection), id),
          addMetadata(value, appName, false),
        )
      } else {
        const otherCollection = otherCollections[name]
        const ref = getCollectionRef(otherCollection)
        batch.update(doc(ref, id), addMetadata(value, appName, false))
      }
    })
  }

  await batch.commit()
}

export const updateItem = async <Data extends AnyObject>(
  appName: AppName,
  collection: Collection<Data>,
  id: string,
  path: string,
  data: any,
): Promise<void> => {
  const { _type } = collection

  if (_type === 'combinedCollection') {
    await updateCombinedCollectionItem(appName, collection, id, path, data)
  } else {
    const baseCollection = getBaseCollection(collection)
    if (!baseCollection) throw new Error('No collection')
    const ref = getCollectionRef(collection)
    const docRef = doc(ref, id)
    const docExists = (await getDoc(docRef))?.exists()
    if (docExists && path) {
      await updateDoc(docRef, path, data)
    } else {
      let withoutMetadata: Partial<WithMetadata<Data>> = {}
      if (path) nestedSet(withoutMetadata, path, data)
      else withoutMetadata = data
      const updated = addMetadata(withoutMetadata, appName, false)
      await setDoc(docRef, updated, { merge: true })
    }
  }
}

export const deleteItem = async <Data extends AnyObject>(
  collection: Collection<Data>,
  id: string,
): Promise<UpdateCallback> => {
  const { _type } = collection
  if (_type === 'combinedCollection') {
    const combinedCollection = collection as CombinedCollection
    const batch = writeBatch(db)
    Object.values(combinedCollection.otherCollections).forEach(other => {
      const baseOtherCollection = getBaseCollection(other)
      batch.delete(doc(getCollectionRef(baseOtherCollection), id))
    })
    const baseIndexCollection = getBaseCollection(combinedCollection.index)
    batch.delete(doc(getCollectionRef(baseIndexCollection), id))
    return batch
      .commit()
      .catch(err => {
        console.error(err)
        throw new Error(`Error deleting item: ${err.message}`)
      })
      .then(() => ({ success: 'Deleted item' }))
  }

  const baseCollection = getBaseCollection(collection as Collection)
  if (!baseCollection) throw new Error('No collection')
  const ref = getCollectionRef(collection)
  const docRef = doc(ref, id)
  try {
    await firebaseDeleteDoc(docRef)
    return { success: 'Deleted item' }
  } catch (err: any) {
    return { error: `Error deleting item: ${err.message}` }
  }
}

export const saveItem = async <Data extends AnyObject>(
  appName: AppName,
  collection: Collection<Data>,
  data: FieldMapValue,
): Promise<UpdateCallback> => {
  if (data.id) {
    return updateItem(appName, collection, data.id, '', data)
      .then(() => ({ success: 'Updated item' }))
      .catch(err => ({ error: `Error updating item: ${err.message}` }))
  }
  return addItem(appName, collection, data).then(id => ({
    success: `Added item with id: ${id}`,
  }))
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const subscribeToItem = <T extends AnyObject>(
  path: string,
  callback: (data: T | null) => void,
  onError: (error: BaseError) => void,
) => {
  const unsubscribe = onSnapshot(
    doc(db, path),
    snapshot => {
      callback(snapshot.data() as T | null)
    },
    onError,
  )
  return unsubscribe
}

export const subscribeToQuery = <T extends AnyObject>(
  query: BaseQuery<T>,
  callback: OnItemsData<T, string>,
  onError: (error: BaseError) => void,
) => {
  const firestoreQuery = toFirestoreQuery(db, toHbQuery(query))
  const unsubscribe = onSnapshot(
    firestoreQuery,
    snapshot => {
      callback(
        snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as DBItem<T>),
        snapshot.docs[snapshot.docs.length - 1]?.id ?? null,
      )
    },
    onError,
  )
  return unsubscribe
}

export const fetchItem = async <T extends AnyObject>(path: string): Promise<DBItem<T> | null> => {
  const docRef = doc(db, path) as DocumentReference<T>
  const docSnap = await getDoc(docRef)
  const data = docSnap.data()
  if (!data) {
    return null
  }
  return { ...data, id: docSnap.id } as DBItem<T>
}

export const fetchItems = async <T extends AnyObject>(
  query: BaseQuery<T>,
): Promise<DBItem<T>[]> => {
  const firestoreQuery = toFirestoreQuery(db, toHbQuery(query))
  const snapshot = await getDocs(firestoreQuery)
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as DBItem<T>)
}

export const updateDocument = async (path: string, data: FieldMapValue): Promise<void> => {
  const docRef = doc(db, path)
  const docSnap = await getDoc(docRef)
  const docData = docSnap.data()
  return docData ? updateDoc(docRef, data) : setDoc(docRef, data)
}
